import classNames from 'classnames';
import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from 'app/hooks';
import Collapsable from 'components/collapsable/Collapsable';
import { doFormMsg, FormMsg, NoFormMsg } from 'components/forms/FormMessage';
import Icon from 'components/icons/Icon';
import { IconSymbol, IconVariant } from 'components/icons/Icon.types';
import { ModalDialog } from 'components/modal/Modal';
import { useModal } from 'components/modal/ModalContext';
import LocaleSelector from 'components/navigation/LocaleSelector';
import { useNavigation } from 'components/navigation/NavigationContext';
import { login } from 'features/auth/authSlice';
import {
  postVerificationCodeAsync,
  signupUserAsync,
} from 'features/user/userSlice';
import { SLUGS } from 'helpers/consts';
import {
  isValidEmail,
  isValidPassword,
  makeCSV,
  pathStartsWith,
} from 'helpers/helpers';
import { useRandomName } from 'helpers/randomName';
import { getPattern } from 'helpers/regex';
import { Link, useParams } from 'react-router-dom';
import { useAppSettings } from 'selectors/appSettingsSelectors';
import { useUser } from 'selectors/userSelectors';
import { ShowPasswordButton } from './ShowPasswordButton';

import '../forms/WizardForm.scss';
import './UserForm.scss';

export default function UserSignupForm() {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { user, locale } = useUser();
  const { settings } = useAppSettings();
  const { openModal, closeModal } = useModal();
  // Steps of the form, in order of appearance
  enum SHOW_STEPS {
    NAME = 1,
    REGCODE,
    EMAIL_PWD,
    CONSENT,
    VERIFICATION,
  }
  type ISignupFormData = {
    username: string;
    email: string;
    password: string;
    registrationCode?: string;
    locale: string;
  };
  const emptyData: ISignupFormData = {
    username: '',
    email: '',
    password: '',
    registrationCode: '',
    locale: 'en',
  };
  const [data, setData] = useState(emptyData);
  const { isAuthenticated } = useUser();
  const [verificationCode, setVerificationCode] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [method, setMethod] = useState<'regcode' | 'noregcode' | undefined>(
    undefined,
  );
  const [userType, setUserType] = useState<'guest' | 'user' | undefined>(
    undefined,
  );
  const [signupType] = useState<'guest' | 'user'>('user');
  const { code } = useParams();
  const { navigate, navigationInProgress } = useNavigation();
  const getRandomName = useRandomName();
  const [consent, setConsent] = useState<boolean>(false);
  const [showStep, setShowStep] = useState<number>(SHOW_STEPS.NAME);
  const [validForm, setValidForm] = useState<boolean>(true);
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const guestTryoutAccessCode = settings.rights.tryoutCode ?? '';

  useEffect(() => {
    if (navigationInProgress()) return;

    if (isAuthenticated) {
      // Replace the first path component with 'join'
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      if (pathParts.length > 0) {
        pathParts[0] = 'join';
        navigate('/' + pathParts.join('/'));
        closeModal();
      } else {
        navigate('/');
      }
      return;
    }

    setUserType('user');
    if (code && pathStartsWith(SLUGS.SIGNUP)) {
      if (user?.id) {
        closeModal();
        navigate('/join/' + code);
      }
      setMethod('regcode');
      setData((prev) => {
        return { ...prev, registrationCode: code };
      });
    }
  }, []);

  function formatRegCode(regcode?: string): string | undefined {
    if (!regcode) return undefined;
    return makeCSV(regcode);
  }

  function hasRegistered(): boolean {
    return user?.id || userId !== null ? true : false;
  }

  function renameAtt(att: string): string {
    switch (att) {
      case 'name':
        return 'username';
    }
    return att;
  }

  const handleChangeData = (e: SyntheticEvent) => {
    doFormMsg(NoFormMsg, setFormMsg);
    const target = e.target as HTMLInputElement;
    setData({
      ...data,
      [renameAtt(target.name)]: target.value,
    });
  };

  const handleChangeCode = (e: SyntheticEvent) => {
    doFormMsg(NoFormMsg, setFormMsg);
    const target = e.target as HTMLInputElement;
    setVerificationCode(target.value);
  };

  const handleVerificationCode = async (userId: string): Promise<boolean> => {
    try {
      await dispatch(
        postVerificationCodeAsync({ userId, code: verificationCode }),
      ).unwrap();
      const loginResponse = await dispatch(login(data)).unwrap();
      doFormMsg(
        {
          message: intl.formatMessage({ id: 'USERS.SIGNUP.CODE_SUCCESS' }),
          success: true,
        },
        setFormMsg,
      );
      setTimeout(() => {
        closeModal();
        if (code === guestTryoutAccessCode) {
          navigate('/tryout');
        } else navigate('/');
      }, 3000);
      return false; // keep modal open
    } catch (error: any) {
      const status = error?.response?.status;
      setValidForm(false);
      if (status === 403) {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'USERS.SIGNUP.CODE_FAILED' }),
            success: false,
          },
          setFormMsg,
        );
      } else if (status === 406) {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'USERS.REGCODE.FAILED' }),
            success: false,
          },
          setFormMsg,
        );
      } else {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
            success: false,
          },
          setFormMsg,
        );
      }
      return false; // keep modal open
    }
  };

  const handleUserSignup = async (): Promise<boolean> => {
    try {
      data.username = data.username.trim();
      data.registrationCode = formatRegCode(data.registrationCode);
      data.locale = locale;
      const response = await dispatch(signupUserAsync({ data })).unwrap();
      const status = response.response?.status;
      if (status === 201) {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'USERS.SIGNUP.SUCCESS_VER' }),
            success: true,
          },
          setFormMsg,
        );
        setUserId(response.response.data._id);
      } else if (status === 202) {
        const incorrectRegcodes: string =
          response?.response?.data?.message?.split(': ')?.[1];
        doFormMsg(
          {
            message: `${intl.formatMessage({
              id: 'USERS.SIGNUP.SUCCESS_VER_REGCODE_LIST',
            })} ${incorrectRegcodes}`,
            success: true,
          },
          setFormMsg,
        );
        setUserId(response.response.data._id);
      }
      setShowStep(SHOW_STEPS.VERIFICATION);
      return false; // keep modal open
    } catch (error: any) {
      const status = error?.response?.status;
      let messageId = 'USERS.SIGNUP.FAILED';
      switch (status) {
        case 406:
          setShowStep(SHOW_STEPS.REGCODE);
          messageId = 'USERS.SIGNUP.FAILED_CODE';
          break;
        case 409:
          setShowStep(SHOW_STEPS.EMAIL_PWD);
          messageId = 'USERS.SIGNUP.EMAIL_IN_USE';
          break;
      }
      doFormMsg(
        {
          message: intl.formatMessage({ id: messageId }),
          success: false,
        },
        setFormMsg,
      );
      return false; // keep modal open
    }
  };

  async function handleSubmit(): Promise<boolean> {
    if (isSubmitting) return false;
    setIsSubmitting(true);
    try {
      if (hasRegistered()) {
        // user has registered but not yet verified
        if (userType === 'user') {
          // signing up as user - check verification code entered
          const result = await handleVerificationCode(userId!);
          setIsSubmitting(false);
          return result;
        } else {
          // signing up as guest - already registered case
          doFormMsg(
            {
              message: intl.formatMessage({ id: 'USERS.SIGNUP.SUCCESS_GUEST' }),
              success: false,
            },
            setFormMsg,
          );
          setTimeout(() => {
            closeModal();
            if (code === guestTryoutAccessCode) {
              navigate('/tryout');
            } else navigate('/');
          }, 3000);
          setIsSubmitting(false);
          return false; // keep modal open
        }
      } else {
        // new registration
        const result = await handleUserSignup();
        setIsSubmitting(false);
        return result;
      }
    } catch (err) {
      setValidForm(false);
      doFormMsg(
        {
          message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
          success: false,
        },
        setFormMsg,
      );
      setIsSubmitting(false);
      throw err;
    }
  }

  function handleMethodCheck(event: ChangeEvent<HTMLInputElement>) {
    setMethod(event.target.name === 'regcode' ? 'regcode' : 'noregcode');
    if (event.target.name === 'noregcode') setUserType('user');
  }

  function handleUserTypeCheck(event: ChangeEvent<HTMLInputElement>) {
    setUserType(event.target.name === 'guest' ? 'guest' : 'user');
  }

  function handleConsentCheck(event: ChangeEvent<HTMLInputElement>) {
    setConsent(event.target.checked);
  }

  const completed = useCallback((): number => {
    if (data.username.length < 6) return 0;
    if (!method) return SHOW_STEPS.NAME;
    if (
      method === 'regcode' &&
      !code &&
      (!data.registrationCode || data.registrationCode.length < 3)
    )
      return SHOW_STEPS.NAME;
    if (!userType) return SHOW_STEPS.REGCODE;
    if (
      userType === 'user' &&
      (data.email.length < 8 || data.password.length < 8)
    )
      return SHOW_STEPS.REGCODE;
    if (consent) return SHOW_STEPS.CONSENT + 1; // Fully completed
    return SHOW_STEPS.EMAIL_PWD;
  }, [data, method, userType, consent]);

  // Helper function to determine next step from NAME step
  const getNextStepFromName = (): number => {
    if (method) {
      if (userType === 'guest') {
        return SHOW_STEPS.CONSENT;
      } else {
        return SHOW_STEPS.EMAIL_PWD;
      }
    }
    return SHOW_STEPS.REGCODE;
  };

  function collapsableOpen(stepNum: number): boolean {
    if (hasRegistered()) return showStep === stepNum;
    return (
      (!validForm && showStep !== SHOW_STEPS.VERIFICATION) ||
      showStep === stepNum
    );
  }

  function disableCollapsable(stepNum: number): boolean {
    if (stepNum === SHOW_STEPS.NAME) return !!user;
    if (stepNum === SHOW_STEPS.REGCODE)
      return !!user || completed() < SHOW_STEPS.NAME;
    if (stepNum === SHOW_STEPS.EMAIL_PWD)
      return !!user || completed() < SHOW_STEPS.REGCODE;
    if (stepNum === SHOW_STEPS.CONSENT)
      return !!user || completed() < SHOW_STEPS.EMAIL_PWD;
    return false;
  }

  let step = SHOW_STEPS.NAME;
  return (
    <ModalDialog
      className="user_form user_signup wizard_form"
      title={
        <>
          <FormattedMessage id="USERS.SIGNUP.FORM_TITLE" />
          <LocaleSelector locale={locale} />
        </>
      }
      okBtn={{
        label: intl.formatMessage({
          id: hasRegistered()
            ? 'X.OK'
            : userType === 'user'
              ? 'USERS.SIGNUP.SUBMIT'
              : 'USERS.GUESTS.SUBMIT',
        }),
        onClick: handleSubmit,
        disabled:
          isSubmitting ||
          completed() < SHOW_STEPS.CONSENT ||
          !consent ||
          (hasRegistered() && !!user),
      }}
      enableEscape
      formMsg={formMsg}
      validated={!validForm}
      setValidForm={setValidForm}
      width="medium"
    >
      {/* User name */}
      <Collapsable dimension="height">
        <Collapsable.Controller
          open={collapsableOpen(SHOW_STEPS.NAME)}
          setOpen={() => {
            if (showStep !== SHOW_STEPS.NAME) setShowStep(SHOW_STEPS.NAME);
          }}
          noIcon={false}
          symbol={step++}
          noPlus={collapsableOpen(SHOW_STEPS.NAME)}
          disabled={disableCollapsable(SHOW_STEPS.NAME)}
        >
          {showStep !== SHOW_STEPS.NAME && data.username ? (
            <FormattedMessage
              id="USERS.SIGNUP.WELCOME_NAMED"
              values={{ name: data.username }}
            />
          ) : (
            <FormattedMessage id={'USERS.SIGNUP.NAME_INSTR'} />
          )}
        </Collapsable.Controller>
        <Collapsable.Content
          open={collapsableOpen(SHOW_STEPS.NAME)}
          dimension="height"
        >
          <Form.Group as={Row} controlId="Displayname">
            <Form.Label column sm="12" md="12" lg="12">
              <FormattedMessage id="USERS.SIGNUP.NAME" />
            </Form.Label>
            <Col sm={12} md={12} lg={12}>
              <Row className="subrow">
                <Form.Control
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={data.username || ''}
                  onChange={handleChangeData}
                  required
                  pattern={getPattern('username')}
                />
                {userType === 'guest' ? (
                  <Button
                    className="right"
                    variant={'outline-secondary'}
                    size={'sm'}
                    onClick={() => {
                      setData({ ...data, username: getRandomName() });
                    }}
                  >
                    <FormattedMessage id="USERS.GUESTS.GIVE_NAME" />
                  </Button>
                ) : null}
                {validForm ? (
                  <Button
                    className="right"
                    variant={
                      data.username?.length < 6
                        ? 'outline-secondary'
                        : 'secondary'
                    }
                    size={'sm'}
                    onClick={() => {
                      if (data.username) setShowStep(getNextStepFromName());
                    }}
                    disabled={data.username?.length < 6}
                  >
                    <FormattedMessage id="X.OK" />
                  </Button>
                ) : null}
                <Form.Text className="text-muted instruction">
                  <FormattedMessage id="USERS.SIGNUP.NAME_HINT" />
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  <FormattedMessage id="USERS.SIGNUP.NAME_FDBCK" />
                </Form.Control.Feedback>
              </Row>
            </Col>
          </Form.Group>
        </Collapsable.Content>
      </Collapsable>
      {/* Reg code */}
      {validForm && code ? null : ( // no need to bother with reg code
        <Collapsable dimension="height">
          <Collapsable.Controller
            open={collapsableOpen(SHOW_STEPS.REGCODE)}
            setOpen={() => {
              if (showStep !== SHOW_STEPS.REGCODE && data.username)
                setShowStep(SHOW_STEPS.REGCODE);
            }}
            noIcon={false}
            symbol={step++}
            noPlus={collapsableOpen(SHOW_STEPS.REGCODE)}
            disabled={disableCollapsable(SHOW_STEPS.REGCODE)}
          >
            <FormattedMessage id={'USERS.SIGNUP.REGCODE_INSTR'} />{' '}
            {method && showStep !== SHOW_STEPS.REGCODE ? (
              method === 'regcode' ? (
                <FormattedMessage id={'X.YES'} />
              ) : (
                <FormattedMessage id={'X.NO'} />
              )
            ) : null}
          </Collapsable.Controller>
          <Collapsable.Content
            open={collapsableOpen(SHOW_STEPS.REGCODE)}
            dimension="height"
          >
            <Form.Group as={Row}>
              <Col sm={12} md={12} lg={12}>
                <Row className="subrow">
                  <Form.Group controlId="noregcode">
                    <Form.Check
                      className="text novalidation"
                      name="noregcode"
                      checked={method === 'noregcode'}
                      onChange={handleMethodCheck}
                      type={'radio'}
                      label={intl.formatMessage({
                        id: 'USERS.SIGNUP.WITHOUT_REGCODE',
                      })}
                      disabled={hasRegistered()}
                    />
                  </Form.Group>
                  <Form.Group controlId="regcode">
                    <Form.Check
                      className="text novalidation"
                      name="regcode"
                      checked={method === 'regcode'}
                      onChange={handleMethodCheck}
                      type={'radio'}
                      label={intl.formatMessage({
                        id: 'USERS.SIGNUP.WITH_REGCODE',
                      })}
                      disabled={hasRegistered()}
                    />
                  </Form.Group>
                  {method === 'regcode' ? (
                    <Form.Control
                      type="text"
                      name="registrationCode"
                      value={data.registrationCode || ''}
                      onChange={handleChangeData}
                      required={method === 'regcode'}
                      placeholder={intl.formatMessage({
                        id: 'USERS.SIGNUP.REGCODE_PLCH',
                      })}
                      minLength={3}
                    />
                  ) : null}
                  {validForm ? (
                    <Button
                      className="right"
                      variant={
                        !method ||
                        (method === 'regcode' &&
                          (!data.registrationCode ||
                            data.registrationCode.length < 3))
                          ? 'outline-secondary'
                          : 'secondary'
                      }
                      size={'sm'}
                      onClick={() => {
                        if (method === 'noregcode' || data.registrationCode)
                          setShowStep(SHOW_STEPS.EMAIL_PWD);
                      }}
                      disabled={
                        !method ||
                        (method === 'regcode' &&
                          (!data.registrationCode ||
                            data.registrationCode.length < 3))
                      }
                    >
                      <FormattedMessage id="X.OK" />
                    </Button>
                  ) : null}
                </Row>
              </Col>
            </Form.Group>
            <Form.Control.Feedback type="invalid">
              <FormattedMessage id="USERS.SIGNUP.REGCODE_FDBCK" />
            </Form.Control.Feedback>
          </Collapsable.Content>
        </Collapsable>
      )}
      {/* Email + Pwd */}
      <Collapsable dimension="height">
        <Collapsable.Controller
          open={collapsableOpen(SHOW_STEPS.EMAIL_PWD)}
          setOpen={() => {
            if (showStep !== SHOW_STEPS.EMAIL_PWD && data.username)
              setShowStep(SHOW_STEPS.EMAIL_PWD);
          }}
          noIcon={false}
          symbol={step++}
          noPlus={collapsableOpen(SHOW_STEPS.EMAIL_PWD)}
          disabled={disableCollapsable(SHOW_STEPS.EMAIL_PWD)}
        >
          {method === 'regcode' ? (
            <>
              <FormattedMessage id={'USERS.GUESTS.INSTR'} />{' '}
              {showStep !== SHOW_STEPS.EMAIL_PWD && userType ? (
                userType === 'guest' ? (
                  <FormattedMessage id="USERS.GUESTS.GUEST" />
                ) : (
                  <FormattedMessage id="USERS.GUESTS.USER" />
                )
              ) : null}
            </>
          ) : (
            <FormattedMessage id={'USERS.SIGNUP.USER_INSTR'} />
          )}
        </Collapsable.Controller>
        <Collapsable.Content
          open={collapsableOpen(SHOW_STEPS.EMAIL_PWD)}
          dimension="height"
        >
          {method === 'regcode' ? (
            <Form.Group as={Row}>
              <Col sm={12} md={12} lg={12}>
                <Row className="subrow">
                  <Form.Group controlId="guest">
                    <Form.Check
                      className="text novalidation"
                      name="guest"
                      checked={userType === 'guest'}
                      onChange={handleUserTypeCheck}
                      type={'radio'}
                      label={intl.formatMessage({ id: 'USERS.GUESTS.GUEST' })}
                      disabled={hasRegistered() || signupType === 'user'}
                    />
                  </Form.Group>
                  <Form.Group controlId="user">
                    <Form.Check
                      className="text novalidation"
                      name="user"
                      checked={userType === 'user'}
                      onChange={handleUserTypeCheck}
                      type={'radio'}
                      label={intl.formatMessage({
                        id: 'USERS.GUESTS.USER',
                      })}
                      disabled={hasRegistered()}
                    />
                  </Form.Group>
                  {userType === 'guest' && validForm ? (
                    <Button
                      className="right"
                      variant={
                        !method ||
                        (method === 'regcode' &&
                          (!data.registrationCode ||
                            data.registrationCode?.length < 3))
                          ? 'outline-secondary'
                          : 'secondary'
                      }
                      size={'sm'}
                      onClick={() => {
                        if (data.registrationCode)
                          setShowStep(SHOW_STEPS.CONSENT);
                      }}
                      disabled={
                        !method ||
                        (method === 'regcode' &&
                          (!data.registrationCode ||
                            data.registrationCode?.length < 3))
                      }
                    >
                      <FormattedMessage id="X.OK" />
                    </Button>
                  ) : null}
                </Row>
              </Col>
            </Form.Group>
          ) : null}
          {userType === 'user' ? (
            <>
              <Form.Group as={Row} controlId="Email">
                <Form.Label column sm="12" md="12" lg="12">
                  <FormattedMessage id="USERS.SIGNUP.EMAIL_INSTR" />
                </Form.Label>
                <Col sm={12} md={12} lg={12}>
                  <Row className="subrow">
                    <Form.Control
                      type="email"
                      name="email"
                      autoComplete="email"
                      value={data.email || ''}
                      onChange={handleChangeData}
                      required
                      pattern={getPattern('email')}
                      disabled={hasRegistered()}
                    />
                    <Form.Text className="text-muted instruction">
                      <FormattedMessage id="USERS.SIGNUP.EMAIL_HINT" />
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                      <FormattedMessage id="USERS.SIGNUP.EMAIL_FDBCK" />
                    </Form.Control.Feedback>
                  </Row>
                </Col>
              </Form.Group>
              <Form.Group as={Row} controlId="Password">
                <Form.Label column sm="12" md="12" lg="12">
                  <FormattedMessage id="USERS.SIGNUP.PASSWORD_INSTR" />
                </Form.Label>
                <Col sm={12} md={12} lg={12}>
                  <Row className="subrow">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete="password"
                      value={data.password || ''}
                      onChange={handleChangeData}
                      required
                      minLength={8}
                      pattern={getPattern('password')}
                      disabled={hasRegistered()}
                    />
                    <ShowPasswordButton
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                    />
                    {validForm ? (
                      <Button
                        className="right"
                        variant={
                          data.password?.length < 8
                            ? 'outline-secondary'
                            : 'secondary'
                        }
                        size={'sm'}
                        onClick={() => {
                          if (isValidPassword(data.password))
                            setShowStep(SHOW_STEPS.CONSENT);
                        }}
                        disabled={
                          !isValidEmail(data.email) ||
                          !isValidPassword(data.password)
                        }
                      >
                        <FormattedMessage id="X.OK" />
                      </Button>
                    ) : null}
                  </Row>
                  <Form.Text className="text-muted instruction">
                    <FormattedMessage id="USERS.SIGNUP.PASSWORD_HINT" />
                  </Form.Text>
                  <Form.Control.Feedback className="feedback" type="invalid">
                    <FormattedMessage id="USERS.SIGNUP.PASSWORD_FDBCK" />
                  </Form.Control.Feedback>
                </Col>
              </Form.Group>
            </>
          ) : null}
        </Collapsable.Content>
      </Collapsable>
      {/* Consent */}
      <Collapsable dimension="height">
        <Collapsable.Controller
          open={collapsableOpen(SHOW_STEPS.CONSENT)}
          setOpen={() => {
            if (showStep !== SHOW_STEPS.CONSENT && data.username)
              setShowStep(SHOW_STEPS.CONSENT);
          }}
          noIcon={false}
          symbol={step++}
          noPlus={collapsableOpen(SHOW_STEPS.CONSENT)}
          disabled={disableCollapsable(SHOW_STEPS.CONSENT)}
        >
          <FormattedMessage id={'USERS.SIGNUP.SUBMIT_INSTR'} />
        </Collapsable.Controller>
        <Collapsable.Content
          open={collapsableOpen(SHOW_STEPS.CONSENT)}
          dimension="height"
        >
          <Container fluid="md">
            <Form.Group as={Row} controlId="consent" className="consent">
              <Form.Check
                className="text novalidation"
                name="consent"
                checked={consent}
                onChange={handleConsentCheck}
                type={'checkbox'}
                label={
                  <>
                    {' '}
                    <FormattedMessage id="USERS.CONSENT.CONSENT" />{' '}
                    <Link to="/consent" target="_blank" className="underlined">
                      <FormattedMessage id={'USERS.CONSENT.CONSENT_LINK'} />
                    </Link>{' '}
                    <Icon
                      symbol={IconSymbol.newWindow}
                      size={14}
                      variant={IconVariant.dark}
                      inline
                    />
                  </>
                }
                disabled={hasRegistered()}
              />
            </Form.Group>
          </Container>
        </Collapsable.Content>
      </Collapsable>
      <Form.Group as={Row} className="form">
        <Col align={'left'} sm={12}>
          {/* <div className="vspaced">
            <ModalDialogLink component={UserLoginForm} closeAll>
              <FormattedMessage id="USERS.SIGNUP.HAVE_ACCOUNT" />
            </ModalDialogLink>
          </div> */}
          <div className="vspaced">
            <FormattedMessage id="USERS.CONSENT.CONSENT_READ" />{' '}
            <Link to="/consent" target="_blank">
              <FormattedMessage id={'USERS.CONSENT.CONSENT_LINK'} />
            </Link>{' '}
            <Icon
              symbol={IconSymbol.newWindow}
              size={14}
              variant={IconVariant.dark}
              inline
            />
          </div>
        </Col>
      </Form.Group>
      {/* Verification code */}
      <Form.Group
        as={Row}
        controlId="confirmation"
        className={classNames('confirmation', {
          absent: !hasRegistered() || userType === 'guest',
        })}
      >
        <Col sm={12} md={12} lg={12}>
          <div className="subrow row">
            <Form.Label column sm="4" md="4" lg="4">
              <FormattedMessage id="USERS.SIGNUP.CODE" />
            </Form.Label>
            <Form.Control
              type="text"
              name="code"
              value={verificationCode || ''}
              onChange={handleChangeCode}
              required
              placeholder={intl.formatMessage({ id: 'USERS.SIGNUP.CODE_PLCH' })}
              disabled={!hasRegistered()}
              // hints for OS autofill
              autoComplete="one-time-code"
              inputMode="numeric"
            />
            <Icon
              symbol={IconSymbol.question}
              size={24}
              align="middle"
              hintProps={{
                hint: <FormattedMessage id="USERS.SIGNUP.CODE_HINT" />,
                width: 200,
              }}
            />
          </div>
          <Form.Control.Feedback type="invalid">
            <FormattedMessage id="USERS.SIGNUP.CODE_HINT" />
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
    </ModalDialog>
  );
}
